
import {
  Vue, Component, Prop, Inject, Watch,
} from 'vue-property-decorator';
import momentTimezone from 'moment-timezone';
import moment from 'moment';
import NoResultsFound from '@/features/common/components/NoResultsFound.vue';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import {
  AvailableService, BillingType, InProgress, ReconnectAccounts,
} from '@/features/shipment/types';
import { EstimateDataType } from '@/features/estimate/types';
import { CarrierToFilter } from '@/features/common/components/servicesFilter/FiltersModal.vue';
import { validateEstimateData } from '@/features/estimate/helpers/getEstimate';
import {
  scrollToInPage,
  isValidDeliveryTime,
  compareDeliveryTimeTiebreaker,
  getValidPrice,
} from '@/features/common/helpers';
import { isTotalShipCarrier } from '@/features/common/helpers/carrier';
import LottieAnimation from '@/features/common/components/LottieAnimation.vue';
import BouncyLoader from '@/features/common/components/BouncyLoader.vue';
import { ProductivityTip, getSessionTip } from '@/features/shipment/constants/productivityTips';
import { validateInprogressData } from '../../helpers/createShipment';
import FiltersModal from './FiltersModal.vue';
import ResultsTable from './ResultsTable.vue';
import ProductivityTipComponent from './ProductivityTip.vue';
import TipInfoPanel from './TipInfoPanel.vue';

@Component({
  components: {
    ResultsTable,
    FiltersModal,
    NoResultsFound,
    LottieAnimation,
    BouncyLoader,
    ProductivityTipComponent,
    TipInfoPanel,
  },
})
export default class ResultsCard extends Vue {
  @Inject({ default: false }) readonly isEstimatePage!: boolean;

  @Prop({ default: false }) showDetails!: boolean;

  private showFilters = false;

  private order = false;

  private priceSortingActive = true;

  private dateSortingActive = false;

  private currentSortType: 'price' | 'date' | null = 'price';

  private filteredServices: AvailableService[] = [];

  private isReceiversAcc = false;

  private selectedDate = moment(new Date()).format('YYYY-MM-DD');

  private showDatePicker = false;

  private eastTime = '';

  private carriersToFilter: CarrierToFilter[] = [];

  private formattedSelectedDate = '';

  private isShowResultsNote = false;

  private isLoadingRates = true;

  private currentTip: ProductivityTip | null = null;

  private showTipPanel = false;

  private animationOptions = [
    'searching_for_rates.json',
    'delivery_guy.json',
  ];

  get isGlobalLoading(): boolean {
    return this.$store.state.loading;
  }

  get carrierFiltersFromPref(): string[] | null {
    const userPrefs = this.$store.getters.getUserPreferences;
    return userPrefs ? userPrefs.carrierFilters : null;
  }

  get availableServices(): Array<AvailableService> | null {
    return this.$store.getters.getAvailableServices;
  }

  get reconnectAccountRequired(): ReconnectAccounts | null {
    return this.$store.getters.getReconnectAccountRequired;
  }

  get reconnectCarrierAccounts() {
    if (!this.reconnectAccountRequired) return [];

    return this.reconnectAccountRequired.map((value) => {
      return {
        carrier: value.carrierCode.toUpperCase(),
        show: value?.required,
        reason: value?.reason,
      };
    }).filter((carrier) => carrier.show);
  }

  get services(): Array<AvailableService> {
    let servicesHolder: Array<AvailableService> | null = this.availableServices;

    if (!servicesHolder) return [];

    if (this.carrierFiltersFromPref?.length) {
      servicesHolder = servicesHolder.map(
        (service) => {
          const clone = { ...service };
          const inSavedFilters = this.carrierFiltersFromPref?.includes(service.selectedService);
          clone.show = inSavedFilters || isTotalShipCarrier(service.carrierCode);
          return clone;
        },
      );
    }

    if (this.currentSortType === 'price') {
      return this.sortServicesByPrice(servicesHolder);
    }

    return servicesHolder;
  }

  get isInProgressData(): number {
    return this.$store.getters.getIsInProgressData;
  }

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get estimateData(): EstimateDataType {
    return this.$store.state.estimate.estimateData;
  }

  get randomAnimationPath() {
    const idx = Math.floor(Math.random() * this.animationOptions.length);
    return this.animationOptions[idx];
  }

  @Watch('services', { immediate: true })
  private setInitialFilteredServices() {
    this.createFilteredServices();
  }

  @Watch('isInProgressData', { immediate: true })
  private async replaceData(newValue: number) {
    if (!newValue) return;

    const { billing, shipDate } = this.inProgressData;

    if (!this.isEstimatePage) {
      this.isReceiversAcc = billing.billingType === BillingType.FreightCollect;
    }

    if (this.isReceiversAcc) {
      await this.$nextTick();
      this.openDetails();
    }

    if (shipDate) {
      this.selectedDate = shipDate;
    }

    this.formatSelectedDate();
  }

  private formatSelectedDate(): void {
    if (!this.selectedDate) return;
    this.formattedSelectedDate = moment(this.selectedDate).format('LL');
  }

  async mounted(): Promise<void> {
    scrollToInPage();

    this.currentTip = getSessionTip();
    this.isLoadingRates = true;

    try {
      if (this.isEstimatePage) {
        await validateEstimateData();
      } else {
        await validateInprogressData();
      }
    } finally {
      this.isLoadingRates = false;
    }

    this.getEastTime();

    eventBus.$on(eventNames.TOGGLE_RECEIVERS_ACCOUNT, this.handleApplyReceiversAcc);

    setTimeout(() => {
      this.isShowResultsNote = true;
    }, 2000);
  }

  destroyed(): void {
    eventBus.$off(eventNames.TOGGLE_RECEIVERS_ACCOUNT, this.handleApplyReceiversAcc);
  }

  private async handleApplyDate(): Promise<void> {
    this.isLoadingRates = true;

    try {
      if (this.isEstimatePage) {
        await this.$store.dispatch('getNewEstimateRates', { date: this.selectedDate, showLoading: false });
      } else {
        await this.$store.dispatch('getRates', { date: this.selectedDate, showLoading: false });
        this.$store.dispatch('syncInProgressData');
      }

      this.createFilteredServices();
      this.formatSelectedDate();
      this.getEastTime();
    } finally {
      this.isLoadingRates = false;
    }
  }

  private closePicker() {
    this.showDatePicker = false;
  }

  private createFilteredServices(): void {
    if (!this.carriersToFilter.length) {
      this.filteredServices = this.services.filter((service) => service.show);
    } else {
      this.filteredServices = this.services.filter((service) => {
        if (isTotalShipCarrier(service.carrierCode)) {
          return service.show;
        }

        const carrier = this.carriersToFilter.find((c) => c.name === service.carrierCode);
        const level = carrier?.levels.find((l) => l.value === service.selectedService);

        if (!carrier?.show) {
          return false;
        }

        return level?.show;
      });
    }

    const receiversAccCarrierCode = this.inProgressData.carrierCode;
    if (!this.isReceiversAcc || !receiversAccCarrierCode) {
      this.applyCurrentSort();
      return;
    }

    const receiverNorm = receiversAccCarrierCode.toLowerCase();
    this.filteredServices = this.filteredServices.filter((service) => (
      service.carrierCode.toLowerCase() === receiverNorm
    ));

    this.applyCurrentSort();
  }

  private applyCurrentSort(): void {
    if (!this.currentSortType) return;

    if (this.currentSortType === 'date') {
      this.filteredServices = this.sortServicesByDate(this.filteredServices);
    } else if (this.currentSortType === 'price') {
      this.filteredServices = this.sortServicesByPrice(this.filteredServices);
    }
  }

  private compareByPrice(a: AvailableService, b: AvailableService): number {
    const priceDiff = this.order ? a.total - b.total : b.total - a.total;
    if (priceDiff !== 0) return priceDiff;

    return compareDeliveryTimeTiebreaker(a, b);
  }

  private compareByDate(a: AvailableService, b: AvailableService): number {
    const aTime = new Date(a.deliveryTime.time).getTime();
    const bTime = new Date(b.deliveryTime.time).getTime();
    const timeDiff = this.order ? aTime - bTime : bTime - aTime;
    if (timeDiff !== 0) return timeDiff;

    return getValidPrice(a) - getValidPrice(b);
  }

  private sortServicesByPrice(services: AvailableService[]): AvailableService[] {
    return [...services].sort((a, b) => this.compareByPrice(a, b));
  }

  private sortServicesByDate(services: AvailableService[]): AvailableService[] {
    const validServices: AvailableService[] = [];
    const invalidServices: AvailableService[] = [];

    services.forEach((service) => {
      if (isValidDeliveryTime(service)) {
        validServices.push(service);
      } else {
        invalidServices.push(service);
      }
    });

    validServices.sort((a, b) => this.compareByDate(a, b));

    return [...validServices, ...invalidServices];
  }

  private getEastTime(): void {
    const date = momentTimezone().tz('Canada/Eastern').format('dddd, MMM DD, YYYY, h:mm:ss A');
    this.eastTime = `${date} Eastern Standard Time`;
  }

  private allowedDatePickerDates(date): boolean {
    const biggerThanToday = moment().diff(date, 'days') <= 0;
    const notBiggerThanThreeMonths = moment().diff(date, 'months') > -3;

    if (this.isEstimatePage) {
      return notBiggerThanThreeMonths;
    }

    return biggerThanToday && notBiggerThanThreeMonths;
  }

  private openDetails = () => eventBus.$emit(eventNames.OPEN_ESTIMATE_RESULTS_DETAIL);

  private applyFilters(carriersToFilter: CarrierToFilter[]): void {
    this.carriersToFilter = carriersToFilter;
    this.createFilteredServices();
    this.showFilters = false;
  }

  private sortByDate(toggleOrder = true): void {
    this.currentSortType = 'date';
    this.priceSortingActive = false;
    this.dateSortingActive = true;

    if (toggleOrder) {
      this.order = !this.order;
    }

    this.applyCurrentSort();
  }

  private sortByPrice(toggleOrder = true): void {
    this.currentSortType = 'price';
    this.dateSortingActive = false;
    this.priceSortingActive = true;

    if (toggleOrder) {
      this.order = !this.order;
    }

    this.applyCurrentSort();
  }

  private sort(sortBy: string) {
    const [sortParam, , sortOrder] = sortBy.split('-');

    if (sortParam === 'date') {
      this.order = sortOrder === 'high';
      this.sortByDate(false);
    } else if (sortParam === 'price') {
      this.order = sortOrder === 'high';
      this.sortByPrice(false);
    }
  }

  private handleApplyReceiversAcc(val: boolean): void {
    this.isReceiversAcc = val;
    this.createFilteredServices();
  }
}
